import { Alert, Button, List, Modal, Space, Typography } from "antd";
import WhatsappMessagePreview from "frontend-lib/Components/WhatsappMessagePreview/WhatsappMessagePreview";
import { Customer } from "frontend-lib/DomainTypes/Customer";
import { Template, TemplateType } from "frontend-lib/DomainTypes/Template";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import UploadFileOptiwe, { FileInfo } from "frontend-lib/Components/UploadFileOptiwe";
import TitleTooltip from "frontend-lib/Components/TitleTooltip";
import { serverUrl } from "Requests/Api";

const { Text, Paragraph } = Typography;

interface SendTemplateModalProps {
    selectedCustomer?: Customer;
    selectedTemplate?: Template;
    isVisible: boolean;
    setIsVisible: (visible: boolean) => void;
    handleSendTemplate: (customFileInfo?: FileInfo) => Promise<void>;
}

export default function SendTemplateModal({
    selectedCustomer,
    selectedTemplate,
    isVisible,
    setIsVisible,
    handleSendTemplate,
}: SendTemplateModalProps) {
    const { t } = useTranslation();
    const [customFileInfo, setCustomFileInfo] = useState<FileInfo>();
    const [uploadFileOptiweId, setUploadFileOptiweId] = useState(1);
    const [isSending, setIsSending] = useState(false);

    useEffect(() => setUploadFileOptiweId((id) => id + 1), [isVisible]);

    const clearState = () => {
        setCustomFileInfo(undefined);
    };

    return (
        <Modal
            title={t("newMessages")}
            open={isVisible}
            footer={
                <div>
                    <Button
                        onClick={() => {
                            setIsVisible(false);
                            clearState();
                        }}
                    >
                        {t("cancel")}
                    </Button>
                    <Button
                        type="primary"
                        onClick={async () => {
                            setIsSending(true);
                            try {
                                await handleSendTemplate(customFileInfo);
                                clearState();
                            } finally {
                                setIsSending(false);
                            }
                        }}
                        loading={isSending}
                    >
                        {t("send")}
                    </Button>
                </div>
            }
            onCancel={() => {
                setIsVisible(false);
                clearState();
            }}
        >
            <List>
                <List.Item>
                    <Typography>
                        <Text type="secondary">{t("customer")}</Text>
                        <Paragraph>{selectedCustomer?.name}</Paragraph>
                    </Typography>
                </List.Item>
                <List.Item>
                    <Typography>
                        <Text type="secondary">{t("phone")}</Text>
                        <Paragraph>{selectedCustomer?.phone}</Paragraph>
                    </Typography>
                </List.Item>
                <List.Item>
                    <Typography>
                        <Text type="secondary">{t("outbound.templateName")}</Text>
                        <Paragraph>{selectedTemplate?.name}</Paragraph>
                    </Typography>
                </List.Item>

                {selectedTemplate?.templateType === TemplateType.DOCUMENT && (
                    <List.Item>
                        <Typography style={{ width: "100%" }}>
                            <Text type="secondary">{t("outbound.fileOptional")}</Text>
                            <TitleTooltip content={t("outbound.uploadFileHelper")} />
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <UploadFileOptiwe
                                    key={uploadFileOptiweId}
                                    setUploadedFileInfo={(info) => setCustomFileInfo(info)}
                                    serverUrl={serverUrl!}
                                    translations={{
                                        fileErrorUploadText: t("fileUploadError"),
                                        fileSuccessfullyUploadedText: t("fileUploadSuccess"),
                                        clickToUploadText: t("clickToUploadFile"),
                                    }}
                                />
                            </div>
                        </Typography>
                    </List.Item>
                )}
                <List.Item>
                    <Typography style={{ width: "100%" }}>
                        <Text type="secondary">{t("messageToSend")}</Text>
                        <WhatsappMessagePreview
                            values={{
                                templateBodyExample: selectedTemplate?.text || "",
                                phoneNumberButtonText: selectedTemplate?.buttons?.find(
                                    (b) => b.type === "PHONE_NUMBER"
                                )?.text,
                                urlButtonText: selectedTemplate?.buttons?.find(
                                    (b) => b.type === "URL"
                                )?.text,
                                quickReplyButtons: selectedTemplate?.buttons
                                    ?.filter((b) => b.type === "QUICK_REPLY")
                                    .map((b) => b.text),
                            }}
                            fileUrl={selectedTemplate?.file?.url}
                        />
                    </Typography>
                </List.Item>
                {customFileInfo && (
                    <List.Item>
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Alert
                                message={t("outbound.templateWillBeSentWithFile", {
                                    fileName: customFileInfo.name,
                                })}
                                type="info"
                            />
                        </Space>
                    </List.Item>
                )}
            </List>
        </Modal>
    );
}
